import React, { useState } from 'react';
import { Power, Loader, AlertCircle } from 'lucide-react';
import ApiService from '../../services/api';

const EnvironmentStateControl = ({ environmentName, currentState, onStateChanged }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleStateChange = async () => {
        setIsLoading(true);
        setError(null);
        const action = currentState === 'Ready' ? 'stop' : 'start';

        try {
            let response;
            if (action === 'stop') {
                response = await ApiService.stopEnvironment(environmentName);
            } else {
                response = await ApiService.startEnvironment(environmentName);
            }

            setIsDialogOpen(false);

            if (onStateChanged && response.data) {
                onStateChanged(response.data);
            }
        } catch (error) {
            console.error(`Failed to ${action} environment:`, error);
            setError(error.message || `Failed to ${action} environment`);
        } finally {
            setIsLoading(false);
        }
    };

    // Determine button text and disabled state based on current environment state
    const getButtonProps = () => {
        switch (currentState) {
            case 'Ready':
                return {
                    text: 'Stop Environment',
                    disabled: false,
                    className: 'bg-red-600 hover:bg-red-700'
                };
            case 'Off':
                return {
                    text: 'Start Environment',
                    disabled: false,
                    className: 'bg-green-600 hover:bg-green-700'
                };
            case 'Transitioning':
                return {
                    text: 'Transitioning...',
                    disabled: true,
                    className: 'bg-yellow-600 cursor-not-allowed'
                };
            default:
                return {
                    text: 'Change Environment State',
                    disabled: false,
                    className: 'bg-blue-600 hover:bg-blue-700'
                };
        }
    };

    const buttonProps = getButtonProps();

    return (
        <>
            <button
                onClick={() => setIsDialogOpen(true)}
                className={`inline-flex items-center px-4 py-2 ${buttonProps.className} text-white rounded-md shadow-sm transition-colors`}
                disabled={buttonProps.disabled || isLoading}
            >
                {currentState === 'Transitioning' ? (
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <Power className="w-4 h-4 mr-2" />
                )}
                {buttonProps.text}
            </button>

            {/* Modal Dialog */}
            {isDialogOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">Confirm State Change</h2>
                            <button
                                onClick={() => setIsDialogOpen(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ×
                            </button>
                        </div>

                        <p className="text-sm text-gray-600 mb-6">
                            {currentState === 'Ready'
                                ? `Are you sure you want to stop the ${environmentName} environment?`
                                : `Are you sure you want to start the ${environmentName} environment?`}
                        </p>

                        {currentState === 'Ready' && (
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                                <div className="flex">
                                    <div className="ml-3">
                                        <p className="text-sm text-yellow-700">
                                            This will stop all ECS services and the RDS database. The environment will be unavailable until restarted.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentState === 'Off' && (
                            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                                <div className="flex">
                                    <div className="ml-3">
                                        <p className="text-sm text-blue-700">
                                            Starting this environment will first start the RDS database and then the ECS services. This may take several minutes.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                                <div className="flex">
                                    <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
                                    <div className="ml-1">
                                        <p className="text-sm text-red-700">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end space-x-2">
                            <button
                                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                onClick={() => setIsDialogOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className={`px-4 py-2 text-white rounded-md ${
                                    isLoading
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : currentState === 'Ready'
                                            ? 'bg-red-600 hover:bg-red-700'
                                            : 'bg-green-600 hover:bg-green-700'
                                }`}
                                onClick={handleStateChange}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className="flex items-center">
                                        <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                        Processing...
                                    </span>
                                ) : (
                                    'Confirm'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EnvironmentStateControl;